import { ReactElement, use } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';

import WanderingBoundaryPassageRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-passage-row-provider-context';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import BlockingPassageWarning from 'features/wandering-boundary-settings/views/wandering-boundary-passage-row/components/blocking-passage-warning';
import styles from './wandering-boundary-passage-row-viewing.module.scss';

const WanderingBoundaryPassageRowViewing = (): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { wanderingBoundaryPassage, isExpanded, setIsExpanded, startEditing } = use(WanderingBoundaryPassageRowProviderContext);
  const {
    detailsState: { removeWanderingBoundaryPassage, isEditing, wanderingBoundaries, detectionLoopDevices, domipDevices },
  } = use(WanderingBoundarySettingsProviderContext);

  const getBoundaryNameById = (id: string | null): string => {
    const boundary = wanderingBoundaries?.find((b) => b.id === id);
    return boundary?.name ?? '';
  };

  const renderDeviceById = (id: string | null): ReactElement => {
    const device = detectionLoopDevices?.find((d) => d.id === id);

    if (!device) {
      return <></>;
    }

    return (
      <div className={`${styles.textDisplay} ${styles.innerTextDisplay}`}>
        <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.detectionLoopNameLabel')}</Typography>
        <Typography variant="caption">{device?.name}</Typography>
        <BreadCrumb showEllipsis={device.breadCrumbs.length > 2} allElementNames={device.breadCrumbs}>
          {device.breadCrumbs.map((name) => (
            <Typography key={name} variant="caption">
              {name}
            </Typography>
          ))}
        </BreadCrumb>
      </div>
    );
  };

  const renderDomipDeviceById = (id: string | null): ReactElement => {
    const device = domipDevices?.find((d) => d.id === id);

    if (!device) {
      return <></>;
    }

    return (
      <div className={`${styles.textDisplay} ${styles.innerTextDisplay}`}>
        <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.domipDeviceNameLabel')}</Typography>
        <Typography variant="caption">{device?.name}</Typography>
        <BreadCrumb showEllipsis={device.breadCrumbs.length > 2} allElementNames={device.breadCrumbs}>
          {device.breadCrumbs.map((name) => (
            <Typography key={name} variant="caption">
              {name}
            </Typography>
          ))}
        </BreadCrumb>
      </div>
    );
  };

  const renderButtons = (): ReactElement => {
    return (
      <>
        <IconButton
          component="div"
          className={styles.cancelButton}
          disabled={isEditing}
          onClick={(e) => {
            e.stopPropagation();
            removeWanderingBoundaryPassage(wanderingBoundaryPassage);
          }}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
        <IconButton
          component="div"
          className={styles.checkButton}
          disabled={isEditing}
          onClick={(e) => {
            e.stopPropagation();
            startEditing();
          }}
        >
          <ModeEditOutlineOutlined className={isEditing ? styles.disabledIcon : ''} />
        </IconButton>
      </>
    );
  };

  return (
    <div className={styles.wanderingBoundaryPassageRowViewingContainer}>
      <Accordion expanded={isExpanded} onChange={(_, expanded) => setIsExpanded(expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          {isExpanded ? (
            <div className={styles.accordionSummaryContent}>
              <div className={styles.details}>
                <div className={styles.textDisplay}>
                  <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.nameLabel')}</Typography>
                  <Typography variant="caption">{wanderingBoundaryPassage.name}</Typography>
                </div>
                <div className={styles.boundaryDisplayContainer}>
                  <div className={styles.textDisplay}>
                    <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.wanderingBoundarySelectLabel')}</Typography>
                    <Typography variant="caption">{getBoundaryNameById(wanderingBoundaryPassage.wanderingBoundaryAId)}</Typography>
                    {renderDeviceById(wanderingBoundaryPassage.detectionLoopADeviceId)}
                    {renderDomipDeviceById(wanderingBoundaryPassage.domipADeviceId)}
                    <BlockingPassageWarning fieldName="blockingPassageWarningTimeAInMinutes" />
                  </div>
                  <div className={styles.textDisplay}>
                    <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.wanderingBoundarySelectLabel')}</Typography>
                    <Typography variant="caption">{getBoundaryNameById(wanderingBoundaryPassage.wanderingBoundaryBId)}</Typography>
                    {renderDeviceById(wanderingBoundaryPassage.detectionLoopBDeviceId)}
                    {renderDomipDeviceById(wanderingBoundaryPassage.domipBDeviceId)}
                    <BlockingPassageWarning fieldName="blockingPassageWarningTimeBInMinutes" />
                  </div>
                </div>
              </div>
              <div className={styles.buttonsContainer}>{renderButtons()}</div>
            </div>
          ) : (
            <div className={styles.accordionSummaryContentContracted}>
              <Typography className={styles.wanderingBoundaryName} variant="subtitle1">
                {wanderingBoundaryPassage.name}
              </Typography>
              <div className={styles.buttonsContainerContracted}>{renderButtons()}</div>
            </div>
          )}
        </AccordionSummary>
      </Accordion>
    </div>
  );
};

export default WanderingBoundaryPassageRowViewing;
